import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { OpportunityContactView } from '@api-proxies';
import { AppEditorFactory } from '@factories/app-editor-factory/app-editor-factory';
import { EditorType } from '@factories/app-editor-factory/enum/editor-type';
import { OpportunityService } from '@http/clients/opportunity.service';
import { EntityTypes } from '@models/enums/EntityTypes';
import { AwaiterService } from '@services/awaiter.service';
import { CDK_IMPORTS } from '@utils/standalone-imports';
import {
    EditorItemComponent,
    IconSanitizerService,
    IEditorItemConfig,
    ModalComponentBase,
    ObjectExtensionsService,
    SnackbarNotificationService
} from 'ngx-lib';
import { LoadingButtonComponent } from '../../loading-button/loading-button.component';

interface IModalEditOpportunityContactParameters {
    contact: OpportunityContactView;
}

@Component({
    selector: 'app-modal-edit-opportunity-contact',
    templateUrl: './modal-edit-opportunity-contact.component.html',
    styleUrls: ['./modal-edit-opportunity-contact.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [...CDK_IMPORTS, EditorItemComponent, LoadingButtonComponent]
})
export class ModalEditOpportunityContactComponent
    extends ModalComponentBase<IModalEditOpportunityContactParameters, OpportunityContactView>
    implements OnInit
{
    protected override parameters!: IModalEditOpportunityContactParameters;

    contactFullName!: string;

    contact!: OpportunityContactView;

    factory: AppEditorFactory;

    configItems: IEditorItemConfig[];

    contactInitialState!: OpportunityContactView;

    isLoading: boolean;

    closeIcon: SafeHtml;

    constructor(
        private readonly opportunityService: OpportunityService,
        private readonly awaiter: AwaiterService,
        private readonly snackbarNotificationService: SnackbarNotificationService,
        private readonly objectExtensionService: ObjectExtensionsService,
        private readonly iconSanitizer: IconSanitizerService
    ) {
        super();
        this.factory = new AppEditorFactory();
        this.configItems = [];
        this.closeIcon = this.iconSanitizer.getIcon('closeModal');
        this.isLoading = false;
    }

    ngOnInit(): void {
        this.contactFullName = this.parameters?.contact.fullName ?? '';
        this.contact = this.objectExtensionService.clone(this.parameters?.contact) as OpportunityContactView;
        this.contactInitialState = this.objectExtensionService.clone(
            this.parameters?.contact
        ) as OpportunityContactView;
        this.configItems = [
            {
                label: 'Follow Up on',
                componentId: EditorType.date,
                fieldName: 'followUpDate',
                bindContext: this.contact,
                additionalInfo: { timeEnabled: true }
            },
            {
                label: 'Last Contact',
                componentId: EditorType.date,
                fieldName: 'lastContactDate',
                bindContext: this.contact,
                additionalInfo: { timeEnabled: true }
            },
            {
                label: 'Contact Roles',
                componentId: EditorType.entity,
                fieldName: 'opportunityContactBuildingContactRoles',
                entityId: 'opportunityBuildingContactRole',
                bindContext: this.contact,
                additionalInfo: {
                    entityType: EntityTypes.Contact
                }
            }
        ];
    }

    async saveContact(): Promise<void> {
        await this.awaiter.awaitAction(
            'Saving Contact...',
            async () => {
                const opportunityContactSaved = await this.opportunityService.saveContact(this.contact);

                if (opportunityContactSaved) {
                    this.snackbarNotificationService.success('Contact saved successfully.');
                    this.close(opportunityContactSaved);
                }
            },
            (loading) => (this.isLoading = loading)
        );
    }

    onClose() {
        this.contact = this.contactInitialState;
        this.close(this.contactInitialState);
    }
}
